import React, { useCallback, useEffect, useRef, useState } from 'react';
import BookingFooter from 'day8-react/build/booking-funnel/BookingFooter/BookingFooter';
import history from '../history';
import MobileAppTabBar, { AUTH_PATHS, TAB_BAR_HEIGHT } from './mobile/MobileAppTabBar';
import MobileAppDrawer from './mobile/MobileAppDrawer';
import { supportedCurrencies } from '../config';
import { renewConsent } from '../helpers/consent';
import useCookiebotAvailable from '../hooks/useCookiebotAvailable';
import {
  DEFAULT_VERTICAL_PADDING, onUnreadCount, setLauncherVisible, setVerticalPadding, whenIntercomReady
} from '../helpers/intercom';

const navigationLinks = [
  {
    href: 'https://help.theyachtweek.com/en/',
    label: 'FAQ',
    external: true
  },
  {
    href: '/privacy-policy',
    label: 'Privacy policy',
    external: true
  },
  {
    href: '/legal',
    label: 'Terms & Conditions',
    external: true
  },
  {
    href: '/flexible-booking',
    label: 'Flexible Booking',
    external: true
  }
];

/**
 * Rendered as a navigationLinks entry so it sits on the same row as the other
 * footer links. BookingFooter only renders hrefs, so the click is intercepted
 * on the wrapper below rather than passed as an onClick.
 */
const COOKIE_SETTINGS_HREF = '#cookie-settings';
const cookieSettingsLink = { href: COOKIE_SETTINGS_HREF, label: 'Cookie settings' };

const paymentOptions = {
  visa: true,
  mastercard: true,
  amex: true,
  applePay: true,
  googlePay: true
};

// Keep the launcher clear of the tab bar; height owned by MobileAppTabBar.
const MOBILE_TABBAR_PADDING = TAB_BAR_HEIGHT;

const shouldHideMobileTabBar = path =>
  /\/browse\/yachts\/\d+/.test(path) ||
  /\/browse\/confirm/.test(path) ||
  /\/browse\/cabins\/confirm/.test(path) ||
  /\/plan\/[A-Za-z0-9]+/.test(path) ||
  /\/account\/booking\/\d+/.test(path) ||
  AUTH_PATHS.test(path);

const BookingFooterWrapper = ({ isYachtsAndFriends }) => {
  const [currentPath, setCurrentPath] = useState(history.location.pathname);
  const cookiebotAvailable = useCookiebotAvailable();

  useEffect(() => {
    const unlisten = history.listen(location => {
      setCurrentPath(location.pathname);
    });
    return unlisten;
  }, []);

  const hideFooter = shouldHideMobileTabBar(currentPath);

  const handleFooterClick = event => {
    if (!event.target.closest?.(`a[href="${COOKIE_SETTINGS_HREF}"]`)) return;
    event.preventDefault();
    renewConsent();
  };

  // The launcher is hidden: the messenger is opened from our own S.O.S.
  // triggers instead, so the bubble cannot sit on top of the tab bar or a
  // sticky CTA. The one exception is an unread reply from an agent, which would
  // otherwise be invisible to the guest.
  const unreadCount = useRef(0);
  const syncLauncher = useCallback(() => setLauncherVisible(unreadCount.current > 0), []);

  useEffect(() => whenIntercomReady(() => {
    onUnreadCount(count => {
      unreadCount.current = count;
      syncLauncher();
    });
    syncLauncher();
  }), [syncLauncher]);

  useEffect(() => {
    const syncIntercom = () => {
      const mobileView = window.innerWidth < 768;
      const tabBarVisible = mobileView && !shouldHideMobileTabBar(currentPath);
      document.body.classList.toggle('yw-mobile-tabbar', tabBarVisible);
      setVerticalPadding(tabBarVisible ? MOBILE_TABBAR_PADDING : DEFAULT_VERTICAL_PADDING);
      // Re-asserted per route, not just once on mount: the GTM tag boots
      // Intercom with its own settings and can do so again on a virtual
      // pageview, which would otherwise bring the launcher back for good.
      syncLauncher();
    };

    syncIntercom();
    // Runs again once Intercom exists, since the calls above no-op until then.
    const detachReady = whenIntercomReady(syncIntercom);
    window.addEventListener('resize', syncIntercom);

    return () => {
      detachReady();
      window.removeEventListener('resize', syncIntercom);
      document.body.classList.remove('yw-mobile-tabbar');
    };
  }, [currentPath, syncLauncher]);

  return (
    <div>
      <div
        className="dui-hidden md:dui-block dui-container dui-mx-auto dui-py-4 dui-mt-16"
        onClick={handleFooterClick}
        role="presentation"
      >
        <BookingFooter
          navigationLinks={cookiebotAvailable ? [...navigationLinks, cookieSettingsLink] : navigationLinks}
          paymentOptions={paymentOptions}
        />
      </div>
      {!hideFooter && (
        <>
          <div className="md:dui-hidden" style={{ height: TAB_BAR_HEIGHT }} aria-hidden="true" />
          <MobileAppTabBar isYachtsAndFriends={isYachtsAndFriends} />
          <MobileAppDrawer supportedCurrencies={supportedCurrencies} isYachtsAndFriends={isYachtsAndFriends} />
        </>
      )}
    </div>
  );
};

export default BookingFooterWrapper;
